import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';
import { Modal, ModalFooter } from '../ui/modal';
import { FormGroup, FormLabel, FormInput, FormTextarea, FormSelect, FormCheckbox } from '../ui/form';

function AddProjectGroupModal({ show, handleClose, userId, onProjectGroupAdded }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_favorite: false,
    parent_project_group_id: null,
    is_archived: false,
    user_id: userId  // Changed from owner to user_id to match database schema
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projectGroups, setProjectGroups] = useState([]);
  const addToast = useToast();

  useEffect(() => {
    const fetchProjectGroups = async () => {
      const { data, error } = await supabase
        .from('project_groups')
        .select('id, name')
        .eq('user_id', userId)  // Changed from owner to user_id
        .eq('is_archived', false);
      
      if (error) {
        console.error('Error fetching groups:', error);
        return;
      }
      console.log('Fetched groups:', data);
      setProjectGroups(data);
    };

    if (show) {
      fetchProjectGroups();
    }
  }, [show, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('project_groups')
        .insert([formData])
        .select(`
          *,
          projects (
            id,
            name,
            description
          )
        `);
      if (error) throw error;
      
      addToast('Project group added successfully', 'success');
      onProjectGroupAdded(data[0]);
      handleClose();
    } catch (err) {
      setError(err.message);
      addToast('Error adding project group: ' + err.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={show} onClose={handleClose} title="Add Project Group">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          </div>
        )}

        <FormGroup>
          <FormLabel htmlFor="name" required>Name</FormLabel>
          <FormInput
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
            placeholder="Enter project group name"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="description">Description</FormLabel>
          <FormTextarea
            id="description"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Enter project group description"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="parentGroup">Parent Project Group</FormLabel>
          <FormSelect
            id="parentGroup"
            value={formData.parent_project_group_id || ''}
            onChange={(e) => {
              const value = e.target.value;
              console.log('Selected value:', value);
              setFormData({
                ...formData,
                parent_project_group_id: value === '' ? null : value
              });
            }}
          >
            <option value="">None</option>
            {projectGroups.map(pg => (
              <option key={pg.id} value={pg.id}>
                {pg.name}
              </option>
            ))}
          </FormSelect>
        </FormGroup>

        <FormGroup>
          <FormCheckbox
            id="favorite"
            label="Mark as favorite"
            checked={formData.is_favorite}
            onChange={(e) => setFormData({...formData, is_favorite: e.target.checked})}
          />
        </FormGroup>
        
        <ModalFooter>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Project Group'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default AddProjectGroupModal;